import React, { FC, useState } from "react";
import { Select } from "antd";
import { useTranslation } from "react-i18next";
import "./ReprocessConfirmModal.scss";
import ConfirmationModal from "../../../../common/modals/confirmationModal/ConfirmationModal";
import { DocumentType } from "../../../../documentTypes";

// Real "Auto-detect" option so the field always shows a concrete selected
// value. The confirmed value is undefined for auto-detect / a type id when set.
const AUTO_DETECT = "__auto_detect__";

type Props = {
  open: boolean;
  documentTypes: Array<DocumentType>;
  confirmLoading: boolean;
  onClose: () => void;
  onConfirm: (documentTypeId?: string) => void;
};

const ReprocessConfirmModal: FC<Props> = ({
  open,
  documentTypes,
  confirmLoading,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation("annotationSideControls");

  const [documentTypeId, setDocumentTypeId] = useState<string | undefined>(
    undefined,
  );

  // Reset the choice on dismiss so the next open starts clean (the modal always
  // closes before it reopens) — avoids syncing state with an effect.
  const handleClose = () => {
    setDocumentTypeId(undefined);
    onClose();
  };

  return (
    <ConfirmationModal
      open={open}
      type="danger"
      title={t("reprocessModal.title")}
      description={t("reprocessModal.description")}
      confirmText={t("reprocessModal.confirm")}
      cancelText={t("reprocessModal.cancel")}
      confirmLoading={confirmLoading}
      onCancel={handleClose}
      onClose={handleClose}
      onConfirm={() => onConfirm(documentTypeId)}
    >
      {documentTypes.length > 1 && (
        <div className="reprocess-modal-field">
          <label className="reprocess-modal-field__label">
            {t("groupBlocksHeader.documentTypeLabel")}
          </label>
          <Select
            className="reprocess-modal-field__select"
            value={documentTypeId ?? AUTO_DETECT}
            onChange={(selected) =>
              setDocumentTypeId(selected === AUTO_DETECT ? undefined : selected)
            }
            options={[
              {
                value: AUTO_DETECT,
                label: (
                  <span className="reprocess-modal-field__auto">
                    <i className="bi bi-magic" />
                    {t("reprocessModal.autoDetect", "Auto-detect")}
                  </span>
                ),
              },
              ...documentTypes.map((documentType) => ({
                value: documentType.id,
                label: documentType.name,
              })),
            ]}
          />
          <div className="reprocess-modal-field__hint">
            {t("reprocessModal.documentTypeHint")}
          </div>
        </div>
      )}
    </ConfirmationModal>
  );
};

export default ReprocessConfirmModal;
